'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import {
  coursePurchaseGeneration,
  resolveCoursePurchase,
  subscribeCoursePurchase,
} from './coursePurchaseCache';

/**
 * A lazily-populated registry of the viewer's course-purchase ownership, keyed
 * by each course's derived `listingId`. Surfaces (the unlock banner + every
 * clip lock-overlay, across the whole bio) `register` the listingIds they care
 * about; each is resolved via the process-wide {@link resolveCoursePurchase}
 * cache — so concurrent providers (bio layout, course detail, clip player) share
 * ONE `/api/bio/course-purchased` request per course instead of one each.
 *
 * Mirrors PopshopRN's global course-order `memory-cache` (uid + listingId): a
 * purchased course reads as owned on the course page AND in every off-course
 * rail, from one resolution per id.
 *
 * `currentUserId`: `undefined` while auth resolves (hold), `null` = anonymous
 * (nothing owned, resolve every id to `false` without a request), a string =
 * the signed-in viewer.
 *
 * Same-session unlock (RN `coursePurchased$` parity): the shared cache's
 * GENERATION is bumped on a `course` checkout success (by the provider), which
 * re-runs the resolve for every tracked id across all providers at once — the
 * just-bought course flips to owned with no reload.
 */
export function useCourseOwnershipRegistry(
  currentUserId: string | null | undefined,
): {
  register: (listingId: string | undefined) => void;
  ownership: (listingId: string | undefined) => boolean | undefined;
} {
  // The current cache generation — a bump (post-purchase) re-renders this hook,
  // driving the generation effect below to re-resolve every registered id.
  const generation = useSyncExternalStore(
    subscribeCoursePurchase,
    coursePurchaseGeneration,
    coursePurchaseGeneration,
  );

  // The settled/pending answer per listingId. `undefined` value = pending;
  // a missing key = not yet registered.
  const [owned, setOwned] = useState<Map<string, boolean | undefined>>(
    () => new Map(),
  );
  // Every id a surface has registered — a ref so registering never re-renders
  // on its own; the `owned` map is the render-driving state.
  const registered = useRef<Set<string>>(new Set());

  const resolveInto = useCallback((uid: string, listingId: string) => {
    void resolveCoursePurchase(uid, listingId).then(purchased => {
      setOwned(prev => {
        if (prev.get(listingId) === purchased) return prev;
        return new Map(prev).set(listingId, purchased);
      });
    });
  }, []);

  // On a viewer / generation change, re-resolve every registered id against the
  // new (uid, generation). Anonymous settles to `false` with no request.
  useEffect(() => {
    if (registered.current.size === 0) return;
    if (currentUserId === undefined) return; // auth resolving — hold

    if (currentUserId === null) {
      setOwned(prev => {
        const next = new Map(prev);
        for (const id of registered.current) next.set(id, false);
        return next;
      });
      return;
    }

    // Signed-in — mark pending, then resolve each known id via the shared cache.
    setOwned(prev => {
      const next = new Map(prev);
      for (const id of registered.current) next.set(id, undefined);
      return next;
    });
    for (const id of registered.current) resolveInto(currentUserId, id);
  }, [currentUserId, generation, resolveInto]);

  const register = useCallback(
    (listingId: string | undefined) => {
      if (listingId == null) return;
      const isNew = !registered.current.has(listingId);
      registered.current.add(listingId);

      // Anonymous / no-listing → settle immediately, no request.
      if (currentUserId === null) {
        setOwned(prev =>
          prev.get(listingId) === false
            ? prev
            : new Map(prev).set(listingId, false),
        );
        return;
      }
      // Auth still resolving — leave it pending; the generation effect picks it
      // up once the viewer is known.
      if (currentUserId === undefined) {
        setOwned(prev =>
          prev.has(listingId) ? prev : new Map(prev).set(listingId, undefined),
        );
        return;
      }
      // Signed-in — resolve the first time this id is registered. The shared
      // cache dedups the actual request across providers + repeat registers.
      if (!isNew) return;
      setOwned(prev =>
        prev.has(listingId) ? prev : new Map(prev).set(listingId, undefined),
      );
      resolveInto(currentUserId, listingId);
    },
    [currentUserId, resolveInto],
  );

  const ownership = useCallback(
    (listingId: string | undefined): boolean | undefined => {
      if (listingId == null) return false;
      return owned.get(listingId);
    },
    [owned],
  );

  // Stable value object — changes only when `register` / `ownership` do (the
  // latter on every `owned` update, which is exactly when consumers must
  // re-read), so the provider passes a referentially-stable context value
  // between ownership changes.
  return useMemo(() => ({ register, ownership }), [register, ownership]);
}
